import { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Button, Chip, Stack, Typography } from '@mui/material'

export type LogLevel = 'log' | 'warn' | 'error'
export type LogEntry = { level: LogLevel; time: string; args: unknown[] }

const LEVEL_COLORS: Record<LogLevel, { bg: string; color: string }> = {
  log: { bg: 'transparent', color: '#333' },
  warn: { bg: '#fff8e1', color: '#8d6e00' },
  error: { bg: '#ffeaea', color: '#c62828' },
}

const formatArg = (arg: unknown): string => {
  if (typeof arg === 'string') return arg
  if (arg instanceof Error) return `${arg.message}\n${arg.stack ?? ''}`
  try {
    return JSON.stringify(arg, null, 2)
  } catch {
    return String(arg)
  }
}

export const useLogEntries = () => {
  const [entries, setEntries] = useState<LogEntry[]>([])

  const addEntry = useCallback((level: LogLevel, args: unknown[]) => {
    const now = new Date()
    const time = now.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
    setEntries((prev) => [...prev, { level, time, args }])
  }, [])

  const clear = useCallback(() => setEntries([]), [])

  return { entries, addEntry, clear }
}

const DEFAULT_HEIGHT = 200
const MIN_HEIGHT = 50

const LogPanel = ({ entries, onClear }: { entries: LogEntry[]; onClear: () => void }) => {
  const [expanded, setExpanded] = useState(false)
  const [height, setHeight] = useState(DEFAULT_HEIGHT)
  const scrollRef = useRef<HTMLDivElement>(null)
  const dragStartRef = useRef<{ y: number; height: number } | null>(null)

  useEffect(() => {
    if (expanded && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [entries, expanded])

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.currentTarget.setPointerCapture(e.pointerId)
      dragStartRef.current = { y: e.clientY, height }
    },
    [height]
  )

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragStartRef.current) return
    setHeight(Math.max(MIN_HEIGHT, dragStartRef.current.height + (dragStartRef.current.y - e.clientY)))
  }, [])

  const onPointerUp = useCallback(() => {
    dragStartRef.current = null
  }, [])

  const errorCount = entries.filter((e) => e.level === 'error').length
  const warnCount = entries.filter((e) => e.level === 'warn').length

  return (
    <Box sx={{ borderTop: 1, borderColor: 'divider', bgcolor: '#f8f9fa', fontSize: 12 }}>
      {expanded && (
        <Box
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          sx={{
            height: 6,
            cursor: 'row-resize',
            borderBottom: 1,
            borderColor: 'divider',
            touchAction: 'none',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        />
      )}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 1.5, py: 0.5, cursor: 'pointer', userSelect: 'none', minHeight: 28 }}
        onClick={() => setExpanded((v) => !v)}
      >
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            Logs
          </Typography>
          <Chip label={entries.length} size="small" sx={{ height: 18, fontSize: 11 }} />
          {errorCount > 0 && (
            <Chip
              label={`${errorCount} error${errorCount !== 1 ? 's' : ''}`}
              size="small"
              sx={{ height: 18, fontSize: 11, bgcolor: '#ffcdd2', color: '#c62828' }}
            />
          )}
          {warnCount > 0 && (
            <Chip
              label={`${warnCount} warning${warnCount !== 1 ? 's' : ''}`}
              size="small"
              sx={{ height: 18, fontSize: 11, bgcolor: '#fff3cd', color: '#8d6e00' }}
            />
          )}
        </Stack>
        <Button
          size="small"
          onClick={(e) => {
            e.stopPropagation()
            onClear()
          }}
          sx={{ minWidth: 0, fontSize: 11, px: 1, py: 0 }}
        >
          Clear
        </Button>
      </Stack>
      {expanded && (
        <Box ref={scrollRef} sx={{ height, overflowY: 'auto', borderTop: 1, borderColor: '#eee', bgcolor: '#fff' }}>
          {entries.length === 0 ? (
            <Typography
              variant="caption"
              sx={{ display: 'block', p: 1.5, color: 'text.secondary', textAlign: 'center' }}
            >
              No log entries
            </Typography>
          ) : (
            entries.map((entry, i) => {
              const { bg, color } = LEVEL_COLORS[entry.level]
              const text = entry.args.map(formatArg).join(' ')
              const prefixMatch = text.match(/^\[([^\]]+)\]\s*/)
              return (
                <Box
                  key={i}
                  sx={{
                    px: 1.5,
                    py: '3px',
                    fontFamily: 'monospace',
                    fontSize: 12,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                    bgcolor: bg,
                    color,
                  }}
                >
                  <Box component="span" sx={{ color: '#999', mr: 1 }}>
                    {entry.time}
                  </Box>
                  {prefixMatch && (
                    <Box component="span" sx={{ color: '#1976d2', mr: 1 }}>
                      {prefixMatch[1]}
                    </Box>
                  )}
                  {prefixMatch ? text.slice(prefixMatch[0].length) : text}
                </Box>
              )
            })
          )}
        </Box>
      )}
    </Box>
  )
}

export default LogPanel
